import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, ArrowRight, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  draft: "bg-blue-50 text-blue-700 border-blue-200",
  sealed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  superseded: "bg-slate-50 text-slate-500 border-slate-200",
};

// Entry card for the Key Return Capsule — lives only in the move-out tab. FREE feature.
export function KeyReturnCapsuleCard({ propertyId }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    api.get(`/properties/${propertyId}/key-return-capsule`)
      .then((r) => ok && setD(r.data)).catch(() => ok && setD(false)).finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [propertyId]);

  if (loading) return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex justify-center theme-card" data-testid="krc-card-loading"><Loader2 className="animate-spin text-brand" size={18} /></div>
  );
  if (!d) return null;

  const hasSealed = !!d.current_sealed;
  const status = d.draft ? (d.ready_to_seal ? "ready" : "in_progress") : (hasSealed ? "sealed" : "not_started");
  const label = { not_started: "Not started", in_progress: "In progress", ready: "Ready to seal", sealed: "Sealed" }[status];
  const styleKey = d.draft ? "draft" : (hasSealed ? "sealed" : "not_started");
  const cta = d.draft ? "Continue capsule" : (hasSealed ? "View sealed capsule" : "Start capsule");

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card" data-testid="krc-card">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><KeyRound size={20} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-heading text-lg font-bold text-navy">Key Return Capsule</h3>
            <span data-testid="krc-card-status" className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLE[styleKey]}`}>{label}</span>
          </div>
          <p className="mt-0.5 text-sm text-[#64748B] leading-snug">Record the final details of your move-out — keys, final-condition photos and supporting return records — in one tamper-evident manifest.</p>
          {hasSealed && (
            <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="krc-card-sealed-note"><ShieldCheck size={13} /> Sealed v{d.current_sealed.version}</p>
          )}
          {d.draft && !d.ready_to_seal && (d.missing_requirements || []).length > 0 && (
            <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700" data-testid="krc-card-missing"><AlertTriangle size={13} /> {d.missing_requirements.length} item(s) needed to seal</p>
          )}
          <Button data-testid="krc-card-open" onClick={() => navigate(`/key-return-capsule/${propertyId}`)}
            className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {cta} <ArrowRight size={15} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
